import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { Observation, Patient } from 'fhir/r4';
import { ObservationReferenceRangeService, ObservationReferenceRangeTemplate, ReferenceRangeSuggestion } from '../observation-reference-range.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { ErrorService } from '../../shared/error.service';
import { ValueToStringPipe } from '../../shared/value-to-string.pipe';

export interface ReferenceRangeSuggestionDialogData {
  observation: Observation;
  patient?: Patient;
}

export interface ReferenceRangeSuggestionDialogResult {
  updatedObservation?: Observation;
  appliedTemplate?: ObservationReferenceRangeTemplate;
}

@Component({
  selector: 'app-reference-range-suggestion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDividerModule,
    ValueToStringPipe
  ],
  templateUrl: './reference-range-suggestion-dialog.component.html',
  styleUrl: './reference-range-suggestion-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationReferenceRangeSuggestionDialogComponent {
  private readonly dialogRef = inject<
    MatDialogRef<ObservationReferenceRangeSuggestionDialogComponent, ReferenceRangeSuggestionDialogResult>
  >(MatDialogRef);
  private readonly dialogData = inject<ReferenceRangeSuggestionDialogData>(MAT_DIALOG_DATA);
  private readonly referenceRanges = inject(ObservationReferenceRangeService);
  private readonly notifications = inject(NotificationService);
  private readonly errorService = inject(ErrorService);

  readonly observation = this.dialogData.observation;
  readonly patient = this.dialogData.patient;

  suggestions: ReferenceRangeSuggestion[] = [];
  loading = false;
  applyInFlightKey: string | null = null;

  readonly displayedColumns = ['type', 'range', 'appliesTo', 'age', 'classification', 'actions'];

  constructor() {
    this.loadSuggestions();
  }

  trackByTemplate(_index: number, template: ReferenceRangeSuggestion): string {
    return this.makeTemplateKey(template);
  }

  rangeDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeRange(range);
  }

  appliesToDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeAppliesTo(range);
  }

  ageDescription(range?: ObservationReferenceRangeTemplate['range']): string {
    return this.referenceRanges.describeAge(range);
  }

  loadSuggestions(): void {
    if (!this.observation?.code?.coding?.length) {
      this.suggestions = [];
      return;
    }
    this.loading = true;
    this.referenceRanges
      .matchRangesForObservation(this.observation, this.patient)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: suggestions => (this.suggestions = suggestions ?? []),
        error: () => {
          this.suggestions = [];
          this.errorService.openandCloseError('Unable to load reference range templates.');
        }
      });
  }

  applyRange(template: ObservationReferenceRangeTemplate): void {
    if (!this.observation?.id) {
      this.errorService.openandCloseError('Save the observation before applying a reference range.');
      return;
    }
    if (!template?.range) {
      this.errorService.openandCloseError('Selected template has no range definition.');
      return;
    }
    const key = this.makeTemplateKey(template);
    this.applyInFlightKey = key;

    this.referenceRanges
      .applyRangeToObservation(this.observation, template.range)
      .pipe(finalize(() => (this.applyInFlightKey = null)))
      .subscribe({
        next: updated => {
          this.notifications.success('Reference range applied to observation.');
          this.dialogRef.close({
            updatedObservation: updated,
            appliedTemplate: template
          });
        },
        error: () => this.errorService.openandCloseError('Failed to apply range. Please try again.')
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  isTemplateApplying(template: ObservationReferenceRangeTemplate | ReferenceRangeSuggestion): boolean {
    return this.applyInFlightKey === this.makeTemplateKey(template);
  }

  classificationLabel(value: ReferenceRangeSuggestion['classification']): string {
    switch (value) {
      case 'low':
        return 'Below range';
      case 'high':
        return 'Above range';
      case 'within':
        return 'Within range';
      default:
        return 'Unknown';
    }
  }

  classificationIcon(value: ReferenceRangeSuggestion['classification']): string {
    switch (value) {
      case 'low':
        return 'south';
      case 'high':
        return 'north';
      case 'within':
        return 'check_circle';
      default:
        return 'help';
    }
  }

  observationDisplay(): string {
    const code = this.observation?.code;
    if (!code) {
      return 'Observation';
    }
    return code.text || code.coding?.[0]?.display || code.coding?.[0]?.code || 'Observation';
  }

  observationCode(): string {
    const coding = this.observation?.code?.coding?.[0];
    if (!coding) {
      return '';
    }
    return [coding.system, coding.code].filter(Boolean).join(' | ');
  }

  canApplyRanges(): boolean {
    return !!this.observation?.id;
  }

  private makeTemplateKey(template: ObservationReferenceRangeTemplate | ReferenceRangeSuggestion): string {
    return (
      template.id ||
      `${template.code?.system || 'local'}|${template.code?.code || crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`
    );
  }
}
